import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sid) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (!cancelled) setStatus(data);
        if (!cancelled && data.payment_status !== "paid" && tries < 20) {
          setTimeout(() => setTries((t) => t + 1), 2000);
        }
      } catch (e) {
        if (!cancelled) setStatus({ error: e?.response?.data?.detail || "Failed to fetch status" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sid, tries]);

  const paid = status?.payment_status === "paid";
  const failed = status?.status === "failed" || status?.status === "expired";

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-[#131A2B] border border-white/12 p-10 text-center">
        {!status && (
          <>
            <Loader size={48} className="brand-blue mx-auto animate-spin mb-4" />
            <div className="font-display text-2xl uppercase font-black">Confirming…</div>
          </>
        )}
        {paid && (
          <>
            <CheckCircle2 size={64} className="brand-blue mx-auto mb-4" />
            <div className="font-display text-3xl uppercase font-black">Payment Successful</div>
            <div className="text-white/60 mt-3 text-sm">Session {sid?.slice(-10)}</div>
            <div className="mt-8 flex flex-col gap-2">
              <Link to={status?.kind === "tournament_team" ? `/tournaments/${status?.metadata?.tournament_id || ''}` : "/parent"} data-testid="back-link" className="bg-brand-blue text-white py-3 font-bold uppercase tracking-widest hover:glow-blue">
                {status?.kind === "tournament_team" ? "Back to Tournament" : "Back to Dashboard"}
              </Link>
            </div>
          </>
        )}
        {failed && (
          <>
            <XCircle size={64} className="text-red-400 mx-auto mb-4" />
            <div className="font-display text-3xl uppercase font-black text-red-400">Payment Failed</div>
            <Link to="/" className="mt-8 inline-block border border-white/20 px-6 py-3 uppercase font-bold text-xs tracking-widest">Home</Link>
          </>
        )}
        {status && !paid && !failed && (
          <>
            <Loader size={48} className="brand-orange mx-auto animate-spin mb-4" />
            <div className="font-display text-2xl uppercase font-black">Still processing…</div>
            <div className="text-white/40 text-xs mt-2">Attempt {tries + 1}/20</div>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5" data-testid="payment-cancel-page">
      <div className="max-w-md w-full bg-[#131A2B] border border-white/12 p-10 text-center">
        <XCircle size={64} className="text-white/40 mx-auto mb-4" />
        <div className="font-display text-3xl uppercase font-black">Cancelled</div>
        <div className="text-white/60 mt-3 text-sm">Your payment was cancelled. No charges were made.</div>
        <Link to="/" className="mt-8 inline-block bg-brand-blue text-white px-6 py-3 uppercase font-bold text-xs tracking-widest">Back Home</Link>
      </div>
    </div>
  );
}
